import { useEffect, useState, useRef } from 'react';

import { isChecked } from '@belong/common';
import { ButtonBase, Toast } from '@belong/ui';
import { DisclosureFormField } from 'accounts/components/form-fields/disclosure';
import { StepFormLayout } from 'accounts/components/step-form-layout';
import { ALL_DISCLOSURES_LIST_ENUMS_AND_TITLES } from 'accounts/constants/home-disclosures';
import { HOME_DISCLOSURES_STRINGS } from 'accounts/constants/strings/home-disclosures.strings';
import { useSetupFlowStep } from 'accounts/hooks/homeowners/use-setup-flow-step-context';
import {
  scrollToFirstServiceError,
  scrollToFirstValidationError,
  transformServiceResponseToFormValues,
  transformDisclosureStructure,
  cleanupHiddenDisclosures,
} from 'accounts/utils/disclosures';
import { CheckboxFinalFormAdapter } from 'components/Checkbox/Checkbox';
import Field from 'components/Field/Field';
import { AnimatedMultiSelectableFinalFormAdapter } from 'components/MultiSelectable/AnimatedMultiSelectable';
import { Row, Col } from 'forkedlibraries/react-bootstrap';
import FormLayout from 'layouts/FormLayout/FormLayout';
import { fetchDisclosureConfigurations } from 'store/redux/home-listing-flow';
import { updateDisclosuresFilesIfNecessary } from 'store/redux/homeowner-set-up-flow/actions';
import { formatString } from 'strings';
import { addressAndUnit } from 'utils/formatAddress';
import { useAppDispatch } from 'store/redux/use-app-dispatch';

export function HomeDisclosures() {
  const {
    currentStep,
    currentStepFormData,
    selectedProperty,
    unitId,
    goPreviousStep,
    handleSaveAndNext,
    handleSave,
    refetchCurrentStepData,
  } = useSetupFlowStep();
  const dispatch = useAppDispatch();
  // Inferred from `null` alone this cannot hold the fetched configurations, nor
  // the `[]` its own error path sets.
  const [disclosureConfigurations, setDisclosureConfigurations] = useState<
    any[] | null
  >(null);
  const [isToastOpen, setIsToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const previousVisibleDisclosuresRef = useRef<string[]>([]);
  const disclosureRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (currentStepFormData != null) return;
    const delayMs = 2500;
    const timeoutId = setTimeout(() => {
      refetchCurrentStepData?.();
    }, delayMs);
    return () => clearTimeout(timeoutId);
  }, [currentStepFormData, refetchCurrentStepData]);

  useEffect(() => {
    const fetchConfigurations = async () => {
      try {
        const { disclosures } = await dispatch(fetchDisclosureConfigurations());

        setDisclosureConfigurations(disclosures);
      } catch (error) {
        console.error('Error fetching disclosure configurations:', error);
        setDisclosureConfigurations([]);
      }
    };

    fetchConfigurations();
  }, [dispatch]);

  const shouldShowDisclosure = (
    disclosureName: any,
    selectedDisclosures: any,
    propertyInfo: any,
  ) => {
    // This disclosure has a legal RI-only scope even while configurations are
    // loading or unavailable, so it must never rely on the fail-open fallback.
    if (
      disclosureName === 'HouseCodeViolationsRI' &&
      propertyInfo.state !== 'RI'
    ) {
      return false;
    }

    if (!disclosureConfigurations || !Array.isArray(disclosureConfigurations)) {
      return true;
    }

    const configs = disclosureConfigurations.find(
      (config: any) => config.name === disclosureName,
    );
    if (!configs || !configs.validation || !configs.validation.showWhenAny) {
      return true; // Show if no configuration found or no conditions
    }

    const { showWhenAny } = configs.validation;

    if (!showWhenAny?.length) {
      return true;
    }

    // Check for notShow conditions - if any condition has notShow: true, don't render
    const hasNotShowCondition = showWhenAny.some(
      (condition: { notShow?: boolean }) => {
        if (condition.notShow === true) {
          return true;
        }
        return false;
      },
    );

    if (hasNotShowCondition) {
      return false;
    }

    // Check for notShowInArea conditions - if the property is in any listed
    // area, don't render. Backend uses this to hide the generic `Mold`
    // disclosure in DC in favor of `IndoorMoldContaminationDC`.
    const isHiddenByArea = showWhenAny.some(
      (condition: { notShowInArea?: { type: string; value: string }[] }) => {
        if (!condition.notShowInArea?.length) {
          return false;
        }

        return condition.notShowInArea.some(
          (areaCondition: { type: string; value: string }) => {
            switch (areaCondition.type) {
              case 'State':
                return propertyInfo.state === areaCondition.value;
              case 'County': {
                // Use includes for county to handle variations like "Montgomery"
                // vs "Montgomery County".
                return (
                  propertyInfo.county
                    ?.toLowerCase()
                    .includes(areaCondition.value.toLowerCase()) ||
                  areaCondition.value
                    .toLowerCase()
                    .includes(propertyInfo.county?.toLowerCase())
                );
              }
              case 'Region':
                return propertyInfo.region === areaCondition.value;
              default:
                return false;
            }
          },
        );
      },
    );

    if (isHiddenByArea) {
      return false;
    }

    // Check each condition in showWhenAny (OR logic between different objects)
    const result = showWhenAny.some((condition: any) => {
      let conditionMet = true;

      // Check requiresMortgage condition
      if (condition.requiresMortgage) {
        conditionMet = conditionMet && !!propertyInfo?.hasMortgage;
      }

      // Rent-stabilization disclosures only apply when the DC Licenses step reported a
      // rent-stabilized RAD status. Per-home fact, so it travels with the step data.
      if (condition.requiresRentStabilized) {
        conditionMet = conditionMet && !!propertyInfo?.isRentStabilized;
      }

      // Check buildingCondition for property type requirements
      if (condition.buildingCondition?.requiredPropertyType) {
        const propertyTypeConditionMet =
          propertyInfo?.propertyType ===
          condition.buildingCondition.requiredPropertyType;

        conditionMet = conditionMet && propertyTypeConditionMet;
      }

      // Check showWhenAllDependentDisclosures (AND logic within this condition)
      if (condition.showWhenAllDependentDisclosures) {
        const allDependenciesMet =
          condition.showWhenAllDependentDisclosures.every(
            (dependentDisclosure: any) => {
              const isSelected =
                selectedDisclosures.includes(dependentDisclosure);
              return isSelected;
            },
          );
        conditionMet = conditionMet && allDependenciesMet;
      }

      // Check showWhenInArea (AND logic within this condition)
      if (condition.showWhenInArea) {
        const areaConditionMet = condition.showWhenInArea.every(
          (areaCondition: any) => {
            switch (areaCondition.type) {
              case 'State': {
                const stateMatch = propertyInfo.state === areaCondition.value;
                return stateMatch;
              }
              case 'County': {
                // Use includes for county to handle variations like "Montgomery" vs "Montgomery County"
                const countyMatch =
                  propertyInfo.county
                    ?.toLowerCase()
                    .includes(areaCondition.value.toLowerCase()) ||
                  areaCondition.value
                    .toLowerCase()
                    .includes(propertyInfo.county?.toLowerCase());
                return countyMatch;
              }
              case 'Region': {
                const regionMatch = propertyInfo.region === areaCondition.value;
                return regionMatch;
              }
              default:
                return false;
            }
          },
        );
        conditionMet = conditionMet && areaConditionMet;
      }

      // Check showWhenPropertyBuiltBeforeOfYear condition
      if (condition.showWhenPropertyBuiltBeforeOfYear) {
        const propertyYear = propertyInfo.propertyYearBuilt;
        const thresholdYear = condition.showWhenPropertyBuiltBeforeOfYear;

        // Only show if property year exists, is a complete 4-digit year, and is before the threshold year
        const isCompleteYear =
          propertyYear && propertyYear.toString().length === 4;
        const propertyBuiltBeforeConditionMet =
          isCompleteYear && propertyYear < thresholdYear;
        conditionMet = conditionMet && propertyBuiltBeforeConditionMet;
      }

      return conditionMet;
    });

    return result;
  };

  const address = currentStep?.property?.address || {};
  const { units } = selectedProperty || {};
  const currentUnitIndex = units?.findIndex(
    (unit: any) => unit.basicInfo.unitId === unitId,
  );

  const disclosuresWithTitles = ALL_DISCLOSURES_LIST_ENUMS_AND_TITLES;

  const getFilteredDisclosuresList = (
    selectedDisclosures: string[] = [],
    formValues: { propertyYearBuilt?: number } = {},
  ) => {
    const propertyInfo = {
      state: selectedProperty?.address?.state,
      county: selectedProperty?.address?.county,
      region: selectedProperty?.coverageRegion?.name,
      hasMortgage: currentStepFormData?.hasMortgage,
      isRentStabilized: currentStepFormData?.isRentStabilized,
      propertyType: selectedProperty?.basicInfo?.propertyType,
      propertyYearBuilt:
        formValues?.propertyYearBuilt || currentStepFormData?.propertyYearBuilt,
    };

    return disclosuresWithTitles.filter((disclosure) =>
      shouldShowDisclosure(disclosure.name, selectedDisclosures, propertyInfo),
    );
  };

  const doneLabel =
    units?.length > 1 &&
    currentUnitIndex !== units?.length - 1 &&
    !units?.[currentUnitIndex + 1]?.isFarOffMoveout
      ? HOME_DISCLOSURES_STRINGS['done_label.units']
      : HOME_DISCLOSURES_STRINGS.done_label;

  function getInitialValues() {
    if (!currentStepFormData) {
      return {};
    }

    // Transform service response data to form structure if needed
    const transformedData =
      transformServiceResponseToFormValues(currentStepFormData);
    const { disclosures = {} } = transformedData;

    if (disclosures.types) {
      Object.keys(disclosures.types).forEach((type) => {
        disclosures.types[type].selected = true;

        // Transform the documents arrays to show them in the NewMediaUploader
        // input. Housing-code disclosures are skipped: their notice files are
        // rebuilt per row (by fileUniqueId), not as one disclosure-level upload.
        if (
          type !== 'HousingCodeViolations' &&
          type !== 'HouseCodeViolationsRI' &&
          disclosures.types[type].documents
        ) {
          const alreadyUploadedFiles = disclosures.types[
            type
          ].documents.flatMap(
            (fileTypeObj: { files?: unknown[] }) => fileTypeObj?.files,
          );

          disclosures.types[type].files = {
            availableMedia: alreadyUploadedFiles,
          };
        }
      });
    } else {
      disclosures.types = {};
    }

    // Add propertyYearBuilt from the disclosures service response if available
    const initialValues: {
      disclosures: { types: Record<string, unknown> };
      propertyYearBuilt?: number;
    } = {
      disclosures,
    };
    if (transformedData?.propertyYearBuilt) {
      initialValues.propertyYearBuilt = transformedData.propertyYearBuilt;
    }

    return initialValues;
  }

  const handleViewDocument = async (values: any) => {
    try {
      const valuesWithFilesUploaded = (await dispatch(
        updateDisclosuresFilesIfNecessary(values),
      )) as Record<string, unknown>;

      // Transform the data for API submission
      const transformedValues = { ...valuesWithFilesUploaded };

      // Transform disclosure structures
      transformDisclosureStructure(transformedValues);

      const { document } = await handleSave(transformedValues);

      if (document?.documentUrl) {
        window.open(document?.documentUrl, '_blank');
      }
    } catch (error) {
      console.error('Error viewing document:', error);

      // Scroll to first error if the error is an array with errorCode
      if (Array.isArray(error) && error.length > 0 && error[0].errorCode) {
        // Add a small delay to ensure the DOM is updated and error banners are shown
        setTimeout(() => scrollToFirstServiceError(error, disclosureRefs), 100);
      }
    }
  };

  async function handleSubmit(values: any) {
    try {
      const valuesWithFilesUploaded = (await dispatch(
        updateDisclosuresFilesIfNecessary(values),
      )) as Record<string, unknown>;

      const transformedValues = { ...valuesWithFilesUploaded };

      // Transform disclosure structures
      transformDisclosureStructure(transformedValues);

      const { document } = await handleSave(transformedValues);

      if (document) {
        document.isSigned = true;

        handleSaveAndNext({ document });
      } else {
        setToastMessage('Document could not be signed.');
        setIsToastOpen(true);
      }
    } catch (error) {
      console.error('Error submitting disclosures:', error);

      // Scroll to first error if the error is an array with errorCode
      if (Array.isArray(error) && error.length > 0 && error[0].errorCode) {
        // Add a small delay to ensure the DOM is updated and error banners are shown
        setTimeout(() => scrollToFirstServiceError(error, disclosureRefs), 100);
      }
    }
  }

  return (
    <>
      <StepFormLayout
        key={currentStepFormData ? 'disclosures-ready' : 'disclosures-loading'}
        handleBackStep={goPreviousStep}
        onSubmit={handleSubmit}
        title={HOME_DISCLOSURES_STRINGS.title}
        subTitle={formatString(HOME_DISCLOSURES_STRINGS.subtitle, {
          address: addressAndUnit(address, address?.unitNumber),
        })}
        doneLabel={doneLabel}
        initialValues={getInitialValues()}
        getForm={({ values, form }) => {
          const currentlySelectedDisclosures = values?.disclosures?.types
            ? Object.keys(values.disclosures.types).filter(
                (key) => values.disclosures.types[key]?.selected === true,
              )
            : [];

          // Get dynamically filtered list based on current form state
          const dynamicFilteredDisclosuresList = getFilteredDisclosuresList(
            currentlySelectedDisclosures,
            values,
          );

          // Get current visible disclosure names for cleanup
          const currentVisibleDisclosures = dynamicFilteredDisclosuresList.map(
            (item) => item.name,
          );

          // Clean up form values for disclosures that are no longer visible
          cleanupHiddenDisclosures(
            currentVisibleDisclosures,
            previousVisibleDisclosuresRef.current,
            form,
          );

          // Update the ref with current visible disclosures
          previousVisibleDisclosuresRef.current = currentVisibleDisclosures;

          // Year built is now captured on the DC Licenses step, so it is no
          // longer asked here for DC. Non-DC flows have no Licenses step, so we
          // keep the inline question as a fallback that still gates their lead
          // disclosures. The DC Water leadmap fields also moved to the Licenses
          // step, so LeadDCWaterWebSite is no longer rendered here.
          const alwaysVisibleItems =
            selectedProperty?.address?.state === 'DC'
              ? []
              : [
                  {
                    name: 'PropertyYearBuilt',
                    title: 'When was your home built?',
                  },
                ];

          // All other disclosures are selectable (LeadDCWaterWebSite moved to the
          // Licenses step, so it is never shown here).
          const selectableDisclosures = dynamicFilteredDisclosuresList.filter(
            (item) => item.name !== 'LeadDCWaterWebSite',
          );

          // Subscribe to form state changes to detect validation errors
          form.subscribe(
            (formState) => {
              // Check if there are validation errors
              if (
                formState.invalid &&
                Object.keys(formState.errors || {}).length > 0
              ) {
                // Add a small delay to ensure validation errors are rendered
                setTimeout(() => scrollToFirstValidationError(), 100);
              }
            },
            { errors: true, invalid: true },
          );

          return (
            <FormLayout.Section>
              <Row>
                <Col md={12}>
                  {/* Always visible sections */}
                  {alwaysVisibleItems.map((item) => {
                    let displayTitle = item.title;

                    // Handle link display for LeadDCWaterWebSite
                    if (
                      'hasLink' in item &&
                      item.hasLink &&
                      'linkText' in item &&
                      item.linkText &&
                      'linkUrl' in item &&
                      item.linkUrl
                    ) {
                      displayTitle = item.title.replace(
                        `(${item.linkText})`,
                        `(<a href="${item.linkUrl}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">${item.linkText}</a>)`,
                      );
                    }

                    return (
                      <div
                        key={item.name}
                        className="border-gray-300 rounded-lg border py-lg"
                        ref={(el) => {
                          disclosureRefs.current[item.name] = el;
                        }}
                      >
                        <div>
                          <div
                            className="text-h3-md font-semibold text-navy"
                            dangerouslySetInnerHTML={{ __html: displayTitle }}
                          />
                        </div>
                        <DisclosureFormField
                          item={item}
                          region={selectedProperty?.coverageRegion?.name}
                          state={selectedProperty?.address?.state}
                          propertyYearBuilt={
                            values.propertyYearBuilt ||
                            currentStepFormData?.propertyYearBuilt
                          }
                        />
                      </div>
                    );
                  })}

                  {/* Selectable disclosures */}
                  {selectableDisclosures.length > 0 && (
                    <Field
                      component={AnimatedMultiSelectableFinalFormAdapter}
                      name="disclosures.types"
                      showTrashButton={false}
                      showCheckmark
                      options={selectableDisclosures.map((item) => {
                        // Handle titles with links
                        let displayTitle = item.title;
                        if (item.hasLink && item.linkText && item.linkUrl) {
                          displayTitle = item.title.replace(
                            `(${item.linkText})`,
                            `(<a href="${item.linkUrl}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">${item.linkText}</a>)`,
                          );
                        }

                        return {
                          ...item,
                          key: item.name,
                          value: item.name,
                          title: displayTitle,
                          formFields: (
                            <DisclosureFormField
                              item={item}
                              region={selectedProperty?.coverageRegion?.name}
                              state={selectedProperty?.address?.state}
                              propertyYearBuilt={
                                values.propertyYearBuilt ||
                                currentStepFormData?.propertyYearBuilt
                              }
                            />
                          ),
                        };
                      })}
                      form={form}
                    />
                  )}
                  <div className="mt-xs w-full pb-sm">
                    <Field
                      component={CheckboxFinalFormAdapter}
                      name="isSigned"
                      className="-pt-xs text-p1 uppercase leading-p1 md:text-body"
                      form={form}
                      alignWithField={false}
                      label={HOME_DISCLOSURES_STRINGS.checkbox_label}
                      validate={isChecked}
                    />
                    <ButtonBase
                      type="button"
                      className="relative -top-2xs ml-lg text-p1 font-semibold uppercase md:top-0 md:text-body"
                      onClick={() => handleViewDocument(values)}
                    >
                      {HOME_DISCLOSURES_STRINGS.view_form}
                    </ButtonBase>
                  </div>
                </Col>
              </Row>
            </FormLayout.Section>
          );
        }}
      />
      <Toast
        isVisible={isToastOpen}
        autocloseDuration={5000}
        variant="danger"
        onClose={() => {
          setIsToastOpen(false);
        }}
      >
        {toastMessage}
      </Toast>
    </>
  );
}
